import React, { useState, useId } from 'react';
import { useLocation } from 'react-router-dom';
import { LockKeyhole, Users, ShieldCheck } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useTeam } from '../context/TeamContext';
import { useWorkspace } from '../context/WorkspaceContext';
import { API_URL } from '../lib/apiUrl';

const box = { background: 'var(--surface-0)', borderWidth: 1, borderStyle: 'solid', borderColor: 'var(--border-subtle)', color: 'var(--text-1)' };

export function WorkspaceBar() {
  const { currentTeam } = useTeam();
  const { pathname } = useLocation();
  const { scope, author, setScope, setAuthor, members, canInspect } = useWorkspace();
  if (!currentTeam || !['/dashboard', '/sesiones', '/gimnasio', '/pizarras', '/pizarra'].includes(pathname)) return null;
  const options = [
    ['mine', 'Mi espacio', LockKeyhole], ['shared', 'Compartido · Todos', Users],
    ...(canInspect ? [['all', 'Todos · Coordinación', ShieldCheck]] : []),
  ];
  return <section aria-label="Espacio de trabajo" className="mb-5 p-3 md:p-4 rounded-2xl" style={box}>
    <div className="flex flex-wrap items-center gap-2">
      <span className="text-xs font-bold mr-2">{currentTeam.name}</span>
      {options.map(([value, label, Icon]) => <button type="button" key={value}
        aria-pressed={scope === value} onClick={() => setScope(value)}
        className="inline-flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-semibold"
        style={{ ...box, ...(scope === value ? { color: 'var(--primary)', background: 'var(--primary-soft)', borderColor: 'var(--primary)' } : {}) }}>
        <Icon size={16} />{label}
      </button>)}
      {scope !== 'mine' && <label className="flex flex-wrap items-center gap-2 text-xs ml-auto">
        Autor
        <select aria-label="Filtrar por entrenador" className="form-input text-sm max-w-[240px]" value={author} onChange={e => setAuthor(e.target.value)}>
          <option value="">Todos los entrenadores</option>
          {members.map(member => <option key={member.user_id} value={member.user_id}>{member.name || 'Entrenador'}</option>)}
        </select>
      </label>}
    </div>
    <p className="text-xs mt-3" style={{ color: 'var(--text-2)' }}>
      {scope === 'mine' ? 'Tus sesiones y pizarras privadas del equipo. El coordinador del club también puede revisarlas.'
        : scope === 'all' ? 'Revisión del coordinador: sesiones y pizarras compartidas y privadas de este equipo.'
        : 'Sesiones y pizarras compartidas con todo el staff de este equipo. Filtra por autor para ver su aportación.'}
      {' '}Plantilla, partidos y microciclos siguen siendo comunes.
    </p>
  </section>;
}

export function VisibilityField({ value = 'private', onChange, disabled = false }) {
  const id = useId();
  return <div className="p-3 rounded-xl my-3" style={box}>
    <label htmlFor={id} className="text-sm font-semibold">Guardar en</label>
    <select id={id} className="form-input text-sm mt-2 w-full" value={value} disabled={disabled}
      onChange={e => onChange(e.target.value)}>
      <option value="private">Mi espacio · solo yo y el coordinador</option>
      <option value="team">Compartido · todo el staff del equipo</option>
    </select>
    <p className="text-xs mt-2" style={{ color: 'var(--text-2)' }}>
      {disabled ? 'Solo el autor o el coordinador pueden cambiar el espacio.'
        : value === 'private' ? 'No lo verán los demás entrenadores. El coordinador del club sí tiene acceso.'
        : 'El staff de este equipo podrá verlo y trabajar sobre él. No se publica en otros equipos ni en Internet.'}
    </p>
  </div>;
}

export function WorkspaceBadge({ item }) {
  return <span className="text-xs inline-block" style={{ color: 'var(--text-2)' }}>
    {item.visibility === 'private' ? 'Privado' : 'Compartido'} · {item.author_name || 'Autor anterior'}
  </span>;
}

export function WorkspaceAction({ item, kind, onChanged }) {
  const [busy, setBusy] = useState(false);
  if (!item.can_change_visibility) return null;
  const isPrivate = item.visibility === 'private';
  const change = async e => {
    e.stopPropagation();
    if (busy) return;
    if (!isPrivate && !window.confirm('¿Retirar del espacio compartido? Solo el autor y el coordinador podrán verlo. Cualquier enlace público quedará desactivado.')) return;
    setBusy(true);
    try {
      await axios.patch(`${API_URL}/api/workspaces/${kind}/${item.id}/visibility`, { visibility: isPrivate ? 'team' : 'private' });
      toast.success(isPrivate ? 'Compartido con el staff de este equipo' : 'Guardado en el espacio del autor');
      onChanged?.();
    } catch (error) { toast.error(error.response?.data?.detail || 'No se pudo cambiar el espacio'); }
    finally { setBusy(false); }
  };
  return <button type="button" onClick={change} disabled={busy}
    className="text-xs font-semibold px-3 py-2 rounded-lg disabled:opacity-50" style={box}>
    {busy ? 'Guardando…' : isPrivate ? 'Compartir con el equipo' : 'Pasar a espacio privado'}
  </button>;
}
